import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminListFeedback, adminDeleteFeedback } from "@/lib/api";
import { Card, SectionTitle, Spinner, EmptyState, ConfirmDialog } from "./kit";

const MOOD = {
    loved: { label: "loved it", emoji: "🍡", cls: "bg-sage/25 border-sage/40" },
    okay: { label: "it was okay", emoji: "🍵", cls: "bg-mustard-soft border-mustard/40" },
    rough: { label: "rough week", emoji: "🌧️", cls: "bg-terracotta-soft/60 border-terracotta/40" },
};

const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
};

// Inbox for "tell Mochi how it went" notes (feedback page + wrap-up email link).
export default function FeedbackSection() {
    const [rows, setRows] = useState(null);
    const [target, setTarget] = useState(null);

    const load = useCallback(async () => {
        try { setRows((await adminListFeedback()).feedback || []); } catch { /* auth gate */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async () => {
        const t = target;
        setTarget(null);
        try {
            await adminDeleteFeedback(t.id);
            toast.success("note tucked away 🍃");
            await load();
        } catch { toast.error("couldn't delete that note"); }
    };

    if (rows === null) return <Spinner label="reading the notes…" />;

    return (
        <div data-testid="admin-feedback">
            <SectionTitle hint="every note testers leave on the feedback page — newest first. Wrap-up emails link here with src=wrapup.">
                Feedback · {rows.length}
            </SectionTitle>
            {rows.length === 0 ? (
                <Card><EmptyState testid="feedback-empty">no notes yet — the day-7 email will nudge testers here 🍡</EmptyState></Card>
            ) : (
                <div className="space-y-3">
                    {rows.map((f) => {
                        const m = f.mood ? MOOD[f.mood] : null;
                        return (
                            <Card key={f.id} className="p-5" data-testid={`feedback-note-${f.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap text-xs text-warm-500">
                                            <span className="font-semibold text-warm-900">{f.display_name || "someone"}</span>
                                            {f.email && <span className="truncate">{f.email}</span>}
                                            {f.is_beta && <span className="rounded-full bg-surface px-2 py-0.5">beta</span>}
                                            <span>· {fmtWhen(f.created_at)}</span>
                                            <span>· via {f.source || "app"}</span>
                                        </div>
                                        <p className="text-warm-900 text-sm mt-2 whitespace-pre-wrap leading-relaxed" data-testid="feedback-note-message">{f.message}</p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {m && (
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border text-warm-900 ${m.cls}`} data-testid={`feedback-mood-pill-${f.mood}`}>
                                                {m.emoji} {m.label}
                                            </span>
                                        )}
                                        <button
                                            onClick={() => setTarget(f)}
                                            className="p-2 rounded-full text-warm-500 hover:text-terracotta hover:bg-surface transition-colors"
                                            aria-label="delete note"
                                            data-testid={`feedback-delete-${f.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
            <ConfirmDialog
                open={!!target}
                onClose={() => setTarget(null)}
                onConfirm={remove}
                title="Delete this note?"
                message="It'll be gone for good — Mochi won't remember it."
                confirmLabel="Delete"
                danger
                testid="feedback-delete-dialog"
            />
        </div>
    );
}
